// src/components/tasks/RoutinesCard.tsx
// Displays schedule templates/routines with quick actions
import React from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { add, play, settingsOutline } from 'ionicons/icons';
import { Card } from '../ui/Card';
import type { ScheduleTemplate, ScheduleCategory } from '../../types';
import './tasks.css';

export interface RoutinesCardProps {
  templates: ScheduleTemplate[];
  loading?: boolean;
  onApplyToday: (template: ScheduleTemplate) => void;
  onEdit: (template: ScheduleTemplate) => void;
  onCreate: () => void;
}

// Category display configuration
const CATEGORY_CONFIG: Record<ScheduleCategory, { label: string; icon: string; color: string }> = {
  morning: { label: 'Morning', icon: '🌅', color: '#f59e0b' },
  after_school: { label: 'After School', icon: '🏫', color: '#10b981' },
  evening: { label: 'Evening', icon: '🌆', color: '#8b5cf6' },
  bedtime: { label: 'Bedtime', icon: '🌙', color: '#6366f1' },
  weekend: { label: 'Weekend', icon: '🎮', color: '#ec4899' },
  anytime: { label: 'Any Time', icon: '⏰', color: '#6b7280' },
};

export const RoutinesCard: React.FC<RoutinesCardProps> = ({
  templates,
  loading = false,
  onApplyToday,
  onEdit,
  onCreate,
}) => {
  // Group templates by category
  const groupedTemplates = templates.reduce((acc, template) => {
    const category = template.schedule_category || 'anytime';
    if (!acc[category]) acc[category] = [];
    acc[category].push(template);
    return acc;
  }, {} as Record<ScheduleCategory, ScheduleTemplate[]>);

  // Get active templates count
  const activeCount = templates.filter(t => t.is_active).length;

  return (
    <Card
      title="Routines"
      subtitle={`${activeCount} active`}
      size="md"
    >
      <div className="routines-card">
        {/* Header with add button */}
        <div className="routines-card__header">
          <button
            className="routines-card__add-btn"
            onClick={onCreate}
            aria-label="Create new routine"
          >
            <IonIcon icon={add} />
          </button>
        </div>
        {loading ? (
          <div className="routines-card__loading">
            <IonSpinner name="crescent" />
          </div>
        ) : templates.length === 0 ? (
          <div className="routines-card__empty">
            <span className="routines-card__empty-icon">📋</span>
            <p>No routines yet</p>
            <button className="routines-card__create-btn" onClick={onCreate}>
              <IonIcon icon={add} />
              Create a Routine
            </button>
          </div>
        ) : (
          <div className="routines-card__list">
            {Object.entries(CATEGORY_CONFIG).map(([category, config]) => {
              const categoryTemplates = groupedTemplates[category as ScheduleCategory];
              if (!categoryTemplates?.length) return null;

              return (
                <div key={category} className="routines-card__category">
                  <div
                    className="routines-card__category-header"
                    style={{ '--category-color': config.color } as React.CSSProperties}
                  >
                    <span className="routines-card__category-icon">{config.icon}</span>
                    <span className="routines-card__category-label">{config.label}</span>
                  </div>

                  {categoryTemplates.map(template => (
                    <div
                      key={template.id}
                      className={`routines-card__item ${!template.is_active ? 'inactive' : ''}`}
                      style={{ '--routine-color': template.color || config.color } as React.CSSProperties}
                    >
                      <div className="routines-card__item-icon">
                        {template.icon || config.icon}
                      </div>

                      <div className="routines-card__item-info">
                        <span className="routines-card__item-name">{template.name}</span>
                        {/* Show multiple assignees or fallback to single assignee */}
                        {template.assignees && template.assignees.length > 0 ? (
                          <div className="routines-card__item-assignees">
                            {template.assignees.map(m => (
                              <span key={m.id} className="routines-card__assignee-chip">
                                {m.avatar_url ? (
                                  <img src={m.avatar_url} alt="" className="routines-card__assignee-avatar" />
                                ) : (
                                  <span className="routines-card__assignee-emoji">{m.avatar_emoji || '👤'}</span>
                                )}
                                <span>{m.nickname || m.name}</span>
                              </span>
                            ))}
                          </div>
                        ) : template.assignee ? (
                          <span className="routines-card__item-assignee">
                            {template.assignee.nickname || template.assignee.name}
                          </span>
                        ) : null}
                        {template.items && (
                          <span className="routines-card__item-count">
                            {template.items.length} tasks
                          </span>
                        )}
                      </div>

                      <div className="routines-card__item-actions">
                        <button
                          className="routines-card__action-btn routines-card__action-btn--apply"
                          onClick={() => onApplyToday(template)}
                          aria-label={`Apply ${template.name} today`}
                          title="Apply Today"
                        >
                          <IonIcon icon={play} />
                        </button>
                        <button
                          className="routines-card__action-btn routines-card__action-btn--edit"
                          onClick={() => onEdit(template)}
                          aria-label={`Edit ${template.name}`}
                          title="Edit"
                        >
                          <IonIcon icon={settingsOutline} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

RoutinesCard.displayName = 'RoutinesCard';

export default RoutinesCard;
